import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import Header from './components/Header';
import IncidentDashboard from './components/IncidentDashboard';
import IncidentPage from './components/IncidentPage';
import AuthCallback from './routes/auth/callback';
import { ToastProvider } from './components/ui/use-toast';
import { AuthProvider } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationStateContext';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './components/ErrorFallback';
import LoadingSkeleton from './components/LoadingSkeleton';
import { logger } from './lib/logger';
import { userAtom, isLoadingAuthAtom, authErrorAtom } from './atoms/auth';
import { userLocationAtom } from './atoms/location';
import { mapStateAtom } from './atoms/map';
import { supabase } from './lib/supabase';
import ProfilePage from './components/ProfilePage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      cacheTime: 1000 * 60 * 5,
      refetchInterval: false,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      networkMode: 'online',
      suspense: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true
    },
  },
});

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userLocation] = useAtom(userLocationAtom);
  const [mapState, setMapState] = useAtom(mapStateAtom);
  const setUser = useSetAtom(userAtom);
  const setIsLoadingAuth = useSetAtom(isLoadingAuthAtom);
  const setAuthError = useSetAtom(authErrorAtom);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        setUser(session?.user ?? null);
      } catch (err) {
        logger.error(err, 'auth_init');
        setAuthError(err instanceof Error ? err : new Error('Auth initialization failed'));
      } finally {
        setIsLoadingAuth(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setAuthError(null);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [setUser, setIsLoadingAuth, setAuthError]);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        navigate('/');
      }}
    >
      <Routes>
        <Route
          path="/"
          element={<IncidentDashboard />}
        />
        <Route
          path="/incident/:slug"
          element={<IncidentPage />}
        />
        <Route
          path="/profile"
          element={<ProfilePage />}
        />
        <Route path="/auth/callback" element={<AuthCallback />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LocationProvider>
            <ToastProvider>
              <Router>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <AppContent />
                </ErrorBoundary>
              </Router>
            </ToastProvider>
          </LocationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}